/**
 * Appointment UI Controls JavaScript
 * Handles UI controls like sort dropdown, zip search, etc.
 */

// Setup accessible sort dropdown with keyboard navigation
function setupAccessibleSortDropdown() {
    const sortTrigger = document.getElementById('sort-dropdown-trigger');
    const dropdown = document.getElementById('sort-dropdown');
    const sortOptions = document.querySelectorAll('.sort-option');
    let focusedOptionIndex = 0;

    if (!sortTrigger || !dropdown || sortOptions.length === 0) return;

    // Click handler for trigger
    sortTrigger.addEventListener('click', function(e) {
        e.stopPropagation();
        toggleDropdown();
    });

    // Keyboard handler for trigger
    sortTrigger.addEventListener('keydown', function(e) {
        switch(e.key) {
            case 'Enter':
            case ' ':
            case 'ArrowDown':
            case 'ArrowUp':
                e.preventDefault();
                openDropdown();
                focusOption(0);
                break;
            case 'Escape':
                closeDropdown();
                break;
        }
    });

    // Setup option event listeners
    sortOptions.forEach((option, index) => {
        // Click handler
        option.addEventListener('click', function(e) {
            selectOption(option.getAttribute('data-sort'));
        });

        // Keyboard handler
        option.addEventListener('keydown', function(e) {
            switch(e.key) {
                case 'Enter':
                case ' ':
                    e.preventDefault();
                    selectOption(option.getAttribute('data-sort'));
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    focusNextOption();
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    focusPreviousOption();
                    break;
                case 'Escape':
                    e.preventDefault();
                    closeDropdown();
                    sortTrigger.focus();
                    break;
                case 'Tab':
                    closeDropdown();
                    break;
            }
        });

        // Mouse enter for hover state
        option.addEventListener('mouseenter', function() {
            focusOption(index);
        });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', function(e) {
        if (!dropdown.contains(e.target)) {
            closeDropdown();
        }
    });

    function toggleDropdown() {
        if (dropdown.classList.contains('open')) {
            closeDropdown();
        } else {
            openDropdown();
        }
    }

    function openDropdown() {
        dropdown.classList.add('open');
        sortTrigger.setAttribute('aria-expanded', 'true');

        // Focus the currently selected option
        const activeOption = document.querySelector('.sort-option.active');
        if (activeOption) {
            const activeIndex = Array.from(sortOptions).indexOf(activeOption);
            focusOption(activeIndex);
        }
    }

    function closeDropdown() {
        dropdown.classList.remove('open');
        sortTrigger.setAttribute('aria-expanded', 'false');
    }

    function focusOption(index) {
        // Remove focus from all options
        sortOptions.forEach(option => {
            option.setAttribute('tabindex', '-1');
            option.classList.remove('focused');
        });

        // Focus the specified option
        if (sortOptions[index]) {
            focusedOptionIndex = index;
            sortOptions[index].setAttribute('tabindex', '0');
            sortOptions[index].classList.add('focused');
            sortOptions[index].focus();
        }
    }

    function focusNextOption() {
        const nextIndex = (focusedOptionIndex + 1) % sortOptions.length;
        focusOption(nextIndex);
    }

    function focusPreviousOption() {
        const prevIndex = (focusedOptionIndex - 1 + sortOptions.length) % sortOptions.length;
        focusOption(prevIndex);
    }

    function selectOption(sortType) {
        // Update current sort
        if (typeof window.appointmentScheduling !== 'undefined') {
            window.appointmentScheduling.currentSortBy = sortType;
        }

        // Update button text
        const sortSelected = document.getElementById('sort-selected');
        if (sortSelected) {
            sortSelected.textContent = sortType === 'distance' ? 'Distance' : 'Date';
        }

        // Update active states
        sortOptions.forEach(option => {
            const isActive = option.getAttribute('data-sort') === sortType;
            option.classList.toggle('active', isActive);
            option.setAttribute('aria-selected', isActive.toString());
        });

        // Close dropdown and return focus to trigger
        closeDropdown();
        sortTrigger.focus();

        // Trigger re-sort and re-render if callback is available
        if (typeof window.appointmentScheduling?.onSortChange === 'function') {
            window.appointmentScheduling.onSortChange(sortType);
        }
    }
}

function setupPrimaryZipSearch() {
    // Setup the search button click handler
    const primaryButton = document.querySelector('#zip-code-fallback .zip-code-button-search');
    if (primaryButton) {
        primaryButton.addEventListener('click', function(e) {
            const zipInput = document.getElementById('appointment-zip-code-search');
            const zipCode = zipInput ? zipInput.value : '';

            if (zipCode && isValidZipLength(zipCode)) {
                // Validate the zip code
                if (isValidUSZip(zipCode)) {
                    // Trigger zip code search if callback is available
                    if (typeof window.appointmentScheduling?.onZipCodeSearch === 'function') {
                        window.appointmentScheduling.onZipCodeSearch(zipCode);
                    }
                } else {
                    // Invalid zip code - prevent default and show error
                    e.preventDefault();
                }
            } else {
                // Incomplete zip code - prevent default
                e.preventDefault();

                // Focus the input
                const zipInput = document.getElementById('appointment-zip-code-search');
                if (zipInput) {
                    zipInput.focus();
                }
            }
        });
    }
}

// Show secondary zip code error message
function showSecondaryZipCodeError(message) {
    const zipInput = document.getElementById('secondary-zip-code-search');
    if (!zipInput) return;

    // Remove any existing error message
    clearSecondaryZipCodeError();

    // Create error message element
    const errorElement = document.createElement('div');
    errorElement.id = 'secondary-zip-code-error-message';
    errorElement.className = 'zip-code-error';
    errorElement.textContent = message;

    // Insert error message after the input
    zipInput.parentNode.insertBefore(errorElement, zipInput.nextSibling);
}

// Clear secondary zip code error message
function clearSecondaryZipCodeError() {
    const existingError = document.getElementById('secondary-zip-code-error-message');
    if (existingError) {
        existingError.remove();
    }
}

function setupSecondaryZipSearch() {
    // Setup the search button click handler
    const secondaryButton = document.getElementById('secondary-search-button');
    if (secondaryButton) {
        // Remove any existing event listeners to prevent duplicates
        const newButton = secondaryButton.cloneNode(true);
        secondaryButton.parentNode.replaceChild(newButton, secondaryButton);

        newButton.addEventListener('click', function(e) {
            const zipInput = document.getElementById('secondary-zip-code-search');
            const zipCode = zipInput ? zipInput.value : '';

            // Clear any existing error messages
            clearSecondaryZipCodeError();

            if (!zipCode) {
                e.preventDefault();
                showSecondaryZipCodeError('Please enter a 5-digit zip code');
                zipInput.focus();
                return;
            }

            if (zipCode.length !== 5) {
                e.preventDefault();
                showSecondaryZipCodeError('Please enter a 5-digit zip code');
                zipInput.focus();
                return;
            }

            if (zipCode && isValidZipLength(zipCode)) {
                // Validate the zip code
                if (isValidUSZip(zipCode)) {
                    // Trigger zip code search if callback is available
                    if (typeof window.appointmentScheduling?.onZipCodeSearch === 'function') {
                        window.appointmentScheduling.onZipCodeSearch(zipCode);
                    }
                } else {
                    // Invalid zip code - prevent default and show error
                    e.preventDefault();
                    showSecondaryZipCodeError('Please enter a valid US zip code');
                    zipInput.focus();
                }
            } else {
                // Incomplete zip code - prevent default
                e.preventDefault();
                showSecondaryZipCodeError('Please enter a 5-digit zip code');
                if (zipInput) {
                    zipInput.focus();
                }
            }
        });

        // Clear error message when user starts typing
        const zipInput = document.getElementById('secondary-zip-code-search');
        if (zipInput) {
            zipInput.addEventListener('input', function() {
                clearSecondaryZipCodeError();
            });
        }
    }
}
